import { inject, Injectable, DOCUMENT } from "@angular/core";
import { IAirline } from "../../types/airline";
import { ILanguage } from "../../types/language";
import { IUserStore } from "../../types/user";
import { CookieService } from "./cookie.service";
import { UserProfile } from "./userProfile";
import { AStorageService } from "./storage.abstract";
import { StorageSessionService } from "./storageSession.service";
import { fromEvent, map } from "rxjs";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";

@Injectable({ providedIn: "root" })
export class StorageService extends AStorageService {

  private readonly _tokenKey = "airtrfx_cms_access_token";
  private readonly _profileKey = "airtrfx_cms_profile";
  private readonly _lastUserKey = "last_user";
  private readonly _userAirlineKey = 'airtrfx_cms_user_airline';
  private readonly _currentAirlinekey = 'airtrfx_cms_current_airline';
  private readonly _currentLanguageKey = 'airtrfx_cms_current_language';
  private readonly _warningEnvironmentTimeKey = 'airtrfx_cms_warning_environment_time';
  private readonly _cookies = inject(CookieService);

  private _window = inject(DOCUMENT).defaultView;
  protected readonly _internalObserver = fromEvent<StorageEvent>(this._window, "storage").pipe(takeUntilDestroyed());
  protected readonly _storage = localStorage;

  /**
   * @deprecated use **StorageSessionService** instead
   */
  public readonly session = inject(StorageSessionService);

  public override readonly get = Object.assign(
    super.get,
    {
      token: () => this.get<string>(this._tokenKey),
      lastUser: () => this.get<string>(this._lastUserKey),
      userAirline: () => this.get<IAirline>(this._userAirlineKey),
      currentAirline: () => this.get<IAirline>(this._currentAirlinekey),
      currentLanguage: () => this.get<ILanguage>(this._currentLanguageKey),
      warningEnvironmentTime: () => this.get<any>(this._warningEnvironmentTimeKey),
      profile: () => {
        const user = this.get<IUserStore>(this._profileKey);
        return user ? new UserProfile(user, this) : null;
      }
    }
  );

  public override readonly set = Object.assign(
    super.set,
    {
      token: (value: string) => this.set(this._tokenKey, value),
      profile: (value: UserProfile) => this.set(this._profileKey, value.toStorage()),
      lastUser: (value: string) => this.set(this._lastUserKey, value),
      userAirline: (value: IAirline) => this.set(this._userAirlineKey, value),
      currentLanguage: (value: ILanguage) => this.set(this._currentLanguageKey, value),
      warningEnvironmentTime: (value: any) => this.set(this._warningEnvironmentTimeKey, value),
      currentAirline: (value: IAirline) => {
        this.set(this._currentAirlinekey, value);
        this._cookies.set.currentTenant(value?.code || "");
      },
    }
  );

  public override readonly remove = Object.assign(
    super.remove,
    {
      token: () => this.remove(this._tokenKey),
      profile: () => this.remove(this._profileKey),
      lastUser: () => this.remove(this._lastUserKey),
      userAirline: () => this.remove(this._userAirlineKey),
      currentLanguage: () => this.remove(this._currentLanguageKey),
      warningEnvironmentTime: () => this.remove(this._warningEnvironmentTimeKey),
      currentAirline: () => {
        this.remove(this._currentAirlinekey);
        this._cookies.remove.currentTenant();
      }
    }
  );

  public override readonly observe = Object.assign(
    super.observe,
    {
      token: () => this.observe<string>(this._tokenKey),
      lastUser: () => this.observe<string>(this._lastUserKey),
      userAirline: () => this.observe<IAirline>(this._userAirlineKey),
      currentAirline: () => this.observe<IAirline>(this._currentAirlinekey),
      currentLanguage: () => this.observe<ILanguage>(this._currentLanguageKey),
      warningEnvironmentTime: () => this.observe<any>(this._warningEnvironmentTimeKey),
      profile: () => this.observe<IUserStore>(this._profileKey).pipe(map((user) => user ? new UserProfile(user, this) : null))
    }
  );
}
